import React, { useState, useEffect } from 'react'
import styles from './styles.module.scss'
import { Field, FieldGroup, FormCard, SubmitButtonMail } from '../../reusable'
import { useClaimData } from '../../../claimProvider'
import { FormPartInterface, SaveValueInterface } from '../../../interfaces'
import { Checkbox } from '../../reusable/checkbox'

export const Review = (props: FormPartInterface) => {
    const { isActive, assignGroup, saveValueFunct } = props
    const [currentStep, setCurrentStep] = useState(0)
    const [postCurrentStep, setPostCurrentStep] = useState(0)
    const [formValid, setFormValid] = useState(false)
    const [validationMessage, setValidationMessage] = useState('')
    const claim = useClaimData()
    const [isMarketingConsentConfirmed, setIsMarketingConsentConfirmed] = useState(false)

    useEffect(() => {
        if (!claim) {
            setFormValid(false)
            return
        }

        const requiredFields = {
            address: ['address_line_one', 'city', 'postcode'],
            personal: ['title', 'firstname', 'lastname', 'dob'],
            contact: ['telephone', 'email'],
        }

        let missingFields = []
        let isValid = true

        for (const field of requiredFields.address) {
            if (!claim[field]) {
                isValid = false
                missingFields.push(field)
            }
        }

        for (const field of requiredFields.personal) {
            if (!claim[field]) {
                isValid = false
                missingFields.push(field)
            }
        }

        for (const field of requiredFields.contact) {
            if (!claim[field]) {
                isValid = false
                missingFields.push(field)
            }
        }

        setFormValid(isValid)

        if (!isValid) {
            setValidationMessage(
                'Some required information is missing. Please go back and complete all required fields.',
            )
        } else {
            setValidationMessage('')
        }
    }, [claim])

    useEffect(() => {
        if (claim?.marketing_consent === 'yes') {
            setIsMarketingConsentConfirmed(true)
        }
    }, [claim])

    const formatDate = (dateString: string) => {
        if (!dateString) return ''

        // Check if the date is in DD/MM/YYYY format
        if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
            // The date is already in the desired UK format, just return it
            return dateString
        }

        // For other formats, try to parse and convert
        try {
            const date = new Date(dateString)
            if (!isNaN(date.getTime())) {
                return date.toLocaleDateString('en-GB')
            }
        } catch (error) {
            console.error('Error formatting date:', error)
        }

        // If we can't parse it, return the original
        return dateString
    }

    const formatPreviousAddresses = () => {
        try {
            const previousAddressesData = claim?.previous_addresses
            if (!previousAddressesData) return null

            let addresses = []
            if (typeof previousAddressesData === 'string') {
                addresses = JSON.parse(previousAddressesData)
            } else if (Array.isArray(previousAddressesData)) {
                addresses = previousAddressesData
            }

            if (!addresses || addresses.length === 0) return null

            return addresses
        } catch (error) {
            console.error('Error parsing previous addresses:', error)
            return null
        }
    }

    const handleBack = () => {
        assignGroup('contact')
    }

    const handleContinue = () => {
        if (formValid) {
            assignGroup('signature')
        }
    }

    const getCurrentStep = () => {
        return isActive === 'review' ? (
            <FieldGroup title="Review Your Information">
                <FormCard assignPostStep={setPostCurrentStep} step={currentStep} onBack={handleBack}>
                    <h3>Please Review Your Information</h3>

                    <div className={styles.reviewSection}>
                        <h3>Address Information</h3>
                        <div className={styles.reviewValue}>
                            {claim?.address_line_one ? <>{claim.address_line_one},</> : ''}
                            {claim?.address_line_two ? (
                                <>
                                    <br />
                                    {claim.address_line_two},
                                </>
                            ) : (
                                ''
                            )}
                            {claim?.city ? (
                                <>
                                    <br />
                                    {claim.city},
                                </>
                            ) : (
                                ''
                            )}
                            {claim?.postcode ? (
                                <>
                                    <br />
                                    {claim.postcode}
                                </>
                            ) : (
                                ''
                            )}
                        </div>

                        {formatPreviousAddresses() && (
                            <div className={styles.reviewValue} style={{ marginTop: '0.75rem' }}>
                                {formatPreviousAddresses()?.map((address: any, index: number) => (
                                    <div key={index} style={{ marginBottom: '10px' }}>
                                        {address.line_one}
                                        {address.line_two && (
                                            <>
                                                <br />
                                                {address.line_two}
                                            </>
                                        )}
                                        {address.line_three && (
                                            <>
                                                <br />
                                                {address.line_three}
                                            </>
                                        )}
                                        <br />
                                        {address.town}
                                        {address.county && `, ${address.county}`}
                                        <br />
                                        {address.postcode}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className={styles.reviewSection}>
                        <h3>Personal Information</h3>
                        <div className={styles.reviewValue}>
                            {claim?.title || ''} {claim?.firstname || ''} {claim?.lastname || ''}
                            <br />
                            {formatDate(claim?.dob || '')}
                        </div>
                    </div>

                    <div className={styles.reviewSection}>
                        <h3>Contact Information</h3>
                        <div className={styles.reviewValue}>
                            {claim?.telephone || ''}
                            <br />
                            {claim?.email || ''}
                        </div>
                    </div>

                    <Field fieldLabel="">
                        <Checkbox
                            fieldName="marketing_consent"
                            label="By pressing submit I consent to Sentinel Legal contacting me regarding this claim via email, phone, or SMS. I understand I can opt out at any time by contacting Sentinel Legal or using the unsubscribe link in any message."
                            initialValue={claim?.marketing_consent}
                            saveValue={(data) => {
                                setIsMarketingConsentConfirmed(data.value === 'yes')
                                return saveValueFunct(data)
                            }}
                        />
                    </Field>

                    {validationMessage && <div className={styles.validationMessage}>{validationMessage}</div>}

                    <div className={styles.dataPolicy}>
                        <p>
                            By submitting this form, you agree to our{' '}
                            <a
                                href="https://www.sentinellegal.co.uk/privacy-policy"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.policyLink}
                            >
                                Privacy Policy
                            </a>{' '}
                            and{' '}
                            <a
                                href="https://www.sentinellegal.co.uk/terms-conditions"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.policyLink}
                            >
                                Terms & Conditions
                            </a>
                            . These explain how we use, store, and protect your data.
                        </p>
                    </div>

                    <div className={styles.buttonContainer}>
                        <SubmitButtonMail
                            text="Continue"
                            btnType="primary"
                            onClick={handleContinue}
                            disabled={!formValid}
                        />
                    </div>
                </FormCard>
            </FieldGroup>
        ) : (
            <></>
        )
    }

    return getCurrentStep()
}
